import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Trophy, Sparkles, TrendingUp, Crown, Shield, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/config/api';

function MedalIcon({ rank }) {
  if (rank === 1) return <span title="Gold" className="text-2xl">🥇</span>;
  if (rank === 2) return <span title="Silver" className="text-2xl">🥈</span>;
  if (rank === 3) return <span title="Bronze" className="text-2xl">🥉</span>;
  return <span className="text-sm text-muted-foreground font-mono">#{rank}</span>;
}

export default function Leaderboard() {
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [mineRes, boardRes] = await Promise.all([
          fetch(`${API}/gamification/me`, { credentials: 'include' }),
          fetch(`${API}/gamification/leaderboard?limit=50`, { credentials: 'include' }),
        ]);
        if (mineRes.ok) setMe(await mineRes.json());
        if (boardRes.ok) setBoard(await boardRes.json());
      } catch (e) {
        toast.error('Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="leaderboard-page">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold mb-2 flex items-center gap-2">
          <Trophy className="h-7 w-7 text-amber-400" /> Leaderboard
        </h1>
        <p className="text-sm text-muted-foreground">
          Earn points by logging doses, contributing to discussions, sharing protocols, and tracking neuro metrics.
        </p>
      </div>

      {loading && (
        <Card className="p-10 text-center text-muted-foreground glass-card">Loading…</Card>
      )}

      {!loading && me && (
        <Card className="p-5 mb-6 glass-card bg-gradient-to-br from-primary/5 to-amber-500/5 border-primary/20" data-testid="my-stats-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-7 w-7 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Your Score</p>
                <p className="text-2xl md:text-3xl font-bold">{me.points.toLocaleString()} pts</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {board?.my_rank ? `Rank #${board.my_rank} of ${board.total_competing}` : 'Earn points to climb the board'}
                </p>
              </div>
            </div>
            {me.streak_days > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/15 text-amber-300">
                <Flame className="h-4 w-4" />
                <span className="font-semibold text-sm">{me.streak_days}-day streak</span>
              </div>
            )}
          </div>

          {/* Stats breakdown */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-5 text-xs">
            <Stat label="Doses" value={me.stats.doses} />
            <Stat label="Posts" value={me.stats.forum_posts} />
            <Stat label="Threads" value={me.stats.forum_threads} />
            <Stat label="Protocols" value={me.stats.protocols} />
            <Stat label="Shared" value={me.stats.marketplace_shares} />
            <Stat label="Neuro" value={me.stats.neuro_logs} />
          </div>

          {/* Badges */}
          {me.badges?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-border" data-testid="my-badges">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Badges earned</p>
              <div className="flex flex-wrap gap-2">
                {me.badges.map((b) => (
                  <span
                    key={b.key}
                    title={b.label}
                    data-testid={`badge-${b.key}`}
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${
                      b.tier === 'gold' ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' :
                      b.tier === 'silver' ? 'bg-slate-400/20 text-slate-300 border-slate-400/40' :
                      'bg-orange-700/20 text-orange-300 border-orange-700/40'
                    }`}
                  >
                    <span>{b.icon}</span>
                    {b.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {!loading && board && (
        <Card className="glass-card overflow-hidden" data-testid="leaderboard-table">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h2 className="font-semibold">Top researchers</h2>
            <span className="ml-auto text-xs text-muted-foreground">{board.total_competing} competing</span>
          </div>
          {board.top.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground text-sm">
              No one has earned points yet. Be the first.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {board.top.map((row) => (
                <li
                  key={row.user_id}
                  data-testid={`leaderboard-row-${row.user_id}`}
                  className={`flex items-center gap-3 p-3 ${me?.user_id === row.user_id ? 'bg-primary/5' : ''}`}
                >
                  <div className="w-10 flex-shrink-0 flex items-center justify-center">
                    <MedalIcon rank={row.rank} />
                  </div>
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={row.picture} />
                    <AvatarFallback>{(row.name || 'U').charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{row.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {row.stats.doses} doses • {row.stats.forum_posts} posts • {row.stats.protocols} protocols
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {row.is_admin && (
                      <Badge className="bg-amber-500/20 text-amber-300 hover:bg-amber-500/20"><Shield className="h-3 w-3" /></Badge>
                    )}
                    {row.is_premium && (
                      <Badge className="bg-primary/20 text-primary hover:bg-primary/20"><Crown className="h-3 w-3" /></Badge>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0 w-20">
                    <p className="font-bold">{row.points.toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">pts</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* How points work */}
      <Card className="mt-6 p-5 glass-card" data-testid="points-explainer">
        <h3 className="font-semibold mb-3">How to earn points</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <PointRow label="Log a dose" pts="1" />
          <PointRow label="Forum reply" pts="3" />
          <PointRow label="Start a thread" pts="5" />
          <PointRow label="Create a protocol" pts="10" />
          <PointRow label="Share to marketplace" pts="25" />
          <PointRow label="Log neuro metrics" pts="50" />
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-2 rounded-lg bg-secondary/30 text-center">
      <p className="text-base md:text-lg font-bold leading-tight">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}

function PointRow({ label, pts }) {
  return (
    <div className="flex items-center justify-between p-2 rounded bg-secondary/30">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-primary">+{pts}</span>
    </div>
  );
}
